// ============================================================
// permissions.ts (frontend mirror)
// MIRROR of backend/src/lib/permissions.ts PERMISSION_KEYS. The backend is the
// source of truth + the authoritative gate; this mirror lets the UI read the
// current user's effective permissions (from getMe → AuthUser.effectivePermissions)
// to gate affordances later (Stage 2/3). KEEP IN SYNC with the backend set.
//
// Tristate + resolution live on the server; the frontend only ever sees the
// already-resolved booleans, never the raw overrides.
// ============================================================
export const PERMISSION_KEYS = [
  'seeFirmWideTasks',
  'seeAllCases',
  'createFirmWideAppointments',
  'inviteAppointmentAttendees',
  'createFirmWideTasks',
  'assignTasksToOthers',
  'createFirmWideCases',
  'assignCasesToOthers',
  'editColleaguesTasks',
  'editOthersAppointments',
  'deleteOthersNotes',
  'pinOthersNotes',
  'deleteCases',
  'deleteOthersDocuments',
  'seeColleaguesBilling',
  'viewAllBilling',
  'approveBills',
  'deleteClients',
  'seeAllClients',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

// The resolved effective-permission map the backend returns on getMe.
export type EffectivePermissions = Record<PermissionKey, boolean>

// Human-readable labels + one-line descriptions for the admin editor UI.
export const PERMISSION_LABELS: Record<PermissionKey, { label: string; help: string }> = {
  seeFirmWideTasks: {
    label: 'See colleagues’ tasks',
    help: 'View tasks belonging to other people in the firm (view only — they can’t edit them).',
  },
  seeAllCases: {
    label: 'See all cases',
    help: 'View every case in the firm, not just cases assigned to them.',
  },
  // Firm default ON. Admins and "See all cases" always can. Never opens a case.
  seeAllClients: {
    label: 'See all clients',
    help: 'See every client’s details, notes and documents, even without access to their cases. Turn off for someone to wall them off from clients they don’t work with.',
  },
  createFirmWideAppointments: {
    label: 'Create firm-wide appointments',
    help: 'Create appointments that apply to the whole firm.',
  },
  inviteAppointmentAttendees: {
    label: 'Invite people to appointments',
    help: 'Add other people as attendees on an appointment.',
  },
  createFirmWideTasks: {
    label: 'Create firm-wide tasks',
    help: 'Assign a task to everyone in the firm.',
  },
  assignTasksToOthers: {
    label: 'Assign tasks to others',
    help: 'Add specific people as assignees on a task.',
  },
  createFirmWideCases: {
    label: 'Create firm-wide cases',
    help: 'Make a case visible to everyone in the firm.',
  },
  assignCasesToOthers: {
    label: 'Assign cases to others',
    help: "Add specific people to a case's handling team.",
  },
  editColleaguesTasks: {
    label: 'Edit colleagues’ tasks',
    help: 'Change tasks they can see but aren’t assigned to. Needs “See colleagues’ tasks”. Doesn’t allow deleting.',
  },
  editOthersAppointments: {
    label: 'Edit others’ appointments',
    help: 'Change or move appointments they aren’t involved in. Doesn’t allow deleting.',
  },
  deleteOthersNotes: {
    label: 'Delete others’ notes',
    help: 'Delete client notes written by other people. Doesn’t allow editing them.',
  },
  pinOthersNotes: {
    label: 'Pin others’ notes',
    help: 'Pin or unpin client notes written by other people. Anyone can pin their own notes.',
  },
  deleteCases: {
    label: 'Delete cases',
    help: 'Delete any case they can see. Without this, only the case’s fee-earner (or an admin) can delete it.',
  },
  deleteOthersDocuments: {
    label: 'Delete others’ documents',
    help: 'Delete documents uploaded by other people. Anyone can delete documents they uploaded themselves.',
  },
  seeColleaguesBilling: {
    label: 'See colleagues’ billing',
    help: 'See the £ value of colleagues’ time entries, and unlock the case Settlement section (CFA, figures and deductions preview — built from everyone’s costs). Everyone sees their own £, and time/units on every entry.',
  },
  // Bill approval: admins always approve; this lets a non-admin approve too.
  // Whether bills need approval at all is the firm switch in Admin › Billing.
  approveBills: {
    label: 'Approve bills',
    help: 'Approve bills sent for approval (or send them back with a note), and reopen or delete them while in approval. Applies when your firm requires approval (Admin › Billing). Admins can always approve.',
  },
  // (Audit batch 3) Admins always can. The safety rules stay: a client with
  // notes or documents → admin only; with cases, time or bills → never.
  deleteClients: {
    label: 'Delete clients',
    help: 'Delete a client record. Only a client with no cases, time entries or bills can be deleted, and one with notes or documents only by an admin. Admins can always delete clients.',
  },
  viewAllBilling: {
    label: 'View all billing',
    help: 'See every bill, credit note and payment in the firm — not just those on cases they can access — and record payments. For a cashier or office manager who isn’t an admin.',
  },
}
// Granted per person only — there's no firm-wide default (inherit = off).
export const PER_USER_ONLY_PERMISSIONS: ReadonlySet<PermissionKey> = new Set<PermissionKey>(['viewAllBilling'])

// A per-user override is tristate: true=Granted, false=Denied, null=Inherit
// (follow the firm default). The stored map only holds true/false; a missing
// key means inherit.
export type PermissionOverride = boolean | null
export type UserPermissionOverrides = Partial<Record<PermissionKey, boolean>>

// ── Admin editor grouping (Option A redesign) ────────────────
// The permissions grouped by AREA so the firm-defaults + per-member
// editors read as Tasks / Cases / Appointments sections, not a flat list.
// Every key in PERMISSION_KEYS appears in exactly one group.
export type PermissionGroupKey = 'tasks' | 'cases' | 'appointments' | 'notes' | 'billing'
export const PERMISSION_GROUPS: {
  key: PermissionGroupKey
  label: string
  icon: 'task' | 'brief' | 'cal' | 'pen' | 'pound'
  perms: { key: PermissionKey; icon: 'eye' | 'globe' | 'uplus' | 'edit' | 'trash' | 'flag' | 'check' }[]
}[] = [
  { key: 'tasks', label: 'Tasks', icon: 'task', perms: [
    { key: 'seeFirmWideTasks', icon: 'eye' },
    { key: 'createFirmWideTasks', icon: 'globe' },
    { key: 'assignTasksToOthers', icon: 'uplus' },
    { key: 'editColleaguesTasks', icon: 'edit' },
  ] },
  { key: 'cases', label: 'Cases', icon: 'brief', perms: [
    { key: 'seeAllCases', icon: 'eye' },
    { key: 'seeAllClients', icon: 'eye' },
    { key: 'createFirmWideCases', icon: 'globe' },
    { key: 'assignCasesToOthers', icon: 'uplus' },
    { key: 'deleteCases', icon: 'trash' },
    { key: 'deleteClients', icon: 'trash' },
  ] },
  { key: 'appointments', label: 'Appointments', icon: 'cal', perms: [
    { key: 'createFirmWideAppointments', icon: 'globe' },
    { key: 'inviteAppointmentAttendees', icon: 'uplus' },
    { key: 'editOthersAppointments', icon: 'edit' },
  ] },
  { key: 'notes', label: 'Client notes', icon: 'pen', perms: [
    { key: 'pinOthersNotes', icon: 'flag' },
    { key: 'deleteOthersNotes', icon: 'trash' },
  ] },
  { key: 'billing', label: 'Documents & billing', icon: 'pound', perms: [
    { key: 'deleteOthersDocuments', icon: 'trash' },
    { key: 'seeColleaguesBilling', icon: 'eye' },
    { key: 'viewAllBilling', icon: 'eye' },
    { key: 'approveBills', icon: 'check' },
  ] },
]
